import { useEffect, useState } from "react";
import { apiRequest, useDevelopmentFixtures } from "../api/client";
import type { ServiceType } from "./types";

export interface AgendaEventFields {
  title: string;
  date: string;
  startTime: string | null;
  endTime: string | null;
  service: ServiceType | null;
  category: "meeting" | "call" | "task" | "other";
  status: "scheduled" | "completed" | "cancelled";
  location: string;
  notes: string;
}
export interface AgendaEvent extends AgendaEventFields {
  id: string;
  version: number;
  createdBy: string | null;
  updatedBy: string | null;
  createdAt: string;
  updatedAt: string;
}
export type AgendaEventWrite = AgendaEventFields & {
  commandId: string;
  expectedVersion?: number;
};
export const eventCategories = {
  meeting: "Reunión",
  call: "Llamada",
  task: "Tarea / recordatorio",
  other: "Otro evento",
};
export const eventStatuses = {
  scheduled: "Programado",
  completed: "Realizado",
  cancelled: "Cancelado",
};
const fixtures = new Map<string, AgendaEvent>();
export const agendaEventsApi = {
  async list(
    from: string,
    to: string,
    service?: ServiceType,
  ): Promise<AgendaEvent[]> {
    if (useDevelopmentFixtures)
      return [...fixtures.values()].filter(
        (e) =>
          e.date >= from && e.date <= to && (!service || e.service === service),
      );
    return (
      await apiRequest<{ data: AgendaEvent[] }>(
        `/api/v1/agenda/events?${new URLSearchParams({ from, to, ...(service ? { service } : {}) })}`,
      )
    ).data;
  },
  async get(id: string): Promise<AgendaEvent> {
    if (useDevelopmentFixtures) {
      const event = fixtures.get(id);
      if (!event) throw new Error("No encontramos este evento.");
      return event;
    }
    return (
      await apiRequest<{ data: AgendaEvent }>(
        `/api/v1/agenda/events/${encodeURIComponent(id)}`,
      )
    ).data;
  },
  async save(id: string | null, input: AgendaEventWrite): Promise<AgendaEvent> {
    if (useDevelopmentFixtures) {
      const { commandId, expectedVersion, ...fields } = input;
      const event: AgendaEvent = {
        ...fields,
        id: id ?? commandId,
        version: (expectedVersion ?? 0) + 1,
        createdBy: null,
        updatedBy: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      fixtures.set(event.id, event);
      return event;
    }
    return (
      await apiRequest<{ data: AgendaEvent }>(
        `/api/v1/agenda/events${id ? `/${encodeURIComponent(id)}` : ""}`,
        { method: id ? "PATCH" : "POST", body: JSON.stringify(input) },
      )
    ).data;
  },
};
export function useAgendaEvents(
  from: string,
  to: string,
  service?: ServiceType,
) {
  const [data, setData] = useState<AgendaEvent[] | null>(null),
    [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setData(null);
    setError("");
    agendaEventsApi
      .list(from, to, service)
      .then((value) => {
        if (active) setData(value);
      })
      .catch((caught) => {
        if (active)
          setError(
            caught instanceof Error
              ? caught.message
              : "No pudimos consultar los eventos.",
          );
      });
    return () => {
      active = false;
    };
  }, [from, to, service, revision]);
  return { data, error, refresh: () => setRevision((value) => value + 1) };
}
